import React from 'react';
import { Box, Button, Chip, Typography } from '@mui/material';
import { faArrowRight, faCopy, faFileAlt, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    DrawerStake,
    NextStep,
    NextStepCtaKind,
    StakeTone,
    nextStepSourceLabel,
} from '../utils/deriveNextStep';
import { visuallyHiddenSx } from '../utils/visuallyHidden';

// Semantic tokens only: the card has to read correctly in both themes.
const STAKE_TONE: Record<StakeTone, { color: string; border: string }> = {
    critical: { color: 'var(--color-status-triage)', border: 'var(--color-status-triage)' },
    urgent: { color: 'var(--color-status-triage)', border: 'var(--color-status-triage)' },
    money: { color: 'var(--color-text-primary)', border: 'var(--color-border)' },
    neutral: { color: 'var(--color-text-secondary)', border: 'var(--color-border)' },
};

const CTA_ICON: Record<NextStepCtaKind, typeof faEnvelope> = {
    open_email: faEnvelope,
    open_document: faFileAlt,
    copy_template: faCopy,
    open_url: faArrowRight,
};

export interface WorkItemNextStepCardProps {
    nextStep: NextStep | null;
    stakes?: DrawerStake[];
    onCta?: (kind: NextStepCtaKind, payload?: string) => void;
}

/**
 * One prepared next step: eyebrow, sentence, at most one CTA, a provenance word.
 * Stakes (due / amount / event) ride along only when there is a sentence to anchor them.
 * Renders nothing when no trustworthy step exists — an empty card is another surface
 * arguing about the item.
 */
const WorkItemNextStepCard: React.FC<WorkItemNextStepCardProps> = ({
    nextStep,
    stakes = [],
    onCta,
}) => {
    if (!nextStep) return null;

    return (
        <Box
            data-testid="WB_NEXT_STEP"
            sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderLeft: '3px solid var(--color-accent)',
                display: 'grid',
                gap: 1.25,
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                <Typography
                    variant="caption"
                    sx={{
                        color: 'var(--color-text-muted)',
                        fontWeight: 700,
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                    }}
                >
                    Next step
                </Typography>
                {stakes.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, justifyContent: 'flex-end' }} data-testid="WB_NEXT_STEP_STAKES">
                        {stakes.map((stake) => {
                            const tone = STAKE_TONE[stake.tone];
                            return (
                                <Chip
                                    key={stake.id}
                                    size="small"
                                    variant="outlined"
                                    label={stake.label}
                                    sx={{
                                        height: 22,
                                        fontSize: '0.65rem',
                                        fontWeight: 700,
                                        color: tone.color,
                                        borderColor: tone.border,
                                        bgcolor: 'transparent',
                                    }}
                                />
                            );
                        })}
                    </Box>
                )}
            </Box>

            <Box sx={{ display: 'grid', gap: 0.5 }}>
                <Typography
                    data-testid="WB_NEXT_STEP_TEXT"
                    variant="subtitle1"
                    component="p"
                    sx={{
                        color: 'var(--color-text-primary)',
                        fontWeight: 700,
                        lineHeight: 1.4,
                    }}
                >
                    {nextStep.text}
                </Typography>
                <Typography
                    data-testid="WB_NEXT_STEP_SOURCE"
                    variant="caption"
                    sx={visuallyHiddenSx}
                >
                    {nextStepSourceLabel(nextStep)}
                </Typography>
            </Box>

            {nextStep.cta && onCta && (
                <Button
                    data-testid="WB_NEXT_STEP_CTA"
                    variant="contained"
                    size="small"
                    onClick={() => onCta(nextStep.cta!.kind, nextStep.cta!.payload)}
                    startIcon={<FontAwesomeIcon icon={CTA_ICON[nextStep.cta.kind]} style={{ fontSize: '0.75rem' }} />}
                    sx={{
                        justifySelf: 'start',
                        minHeight: 44,
                        textTransform: 'none',
                        fontWeight: 700,
                        px: 1.75,
                        bgcolor: 'var(--color-accent)',
                        color: 'var(--color-on-accent)',
                        '&:hover': { bgcolor: 'var(--color-accent-hover)', color: 'var(--color-on-accent-hover)' },
                        '&:focus-visible': { outline: '2px solid var(--color-accent)', outlineOffset: '2px' },
                    }}
                >
                    {nextStep.cta.label}
                </Button>
            )}
        </Box>
    );
};

export default WorkItemNextStepCard;
